/**
 * Wire contract for the admin refunds LIST surface (AUT-118 /
 * PHASE-B-API-7):
 *
 *   - `GET /admin/refunds` → `ListAdminRefundsResultDto`
 *
 * Centralised in `@carcheck/shared` so the api + admin SPA import the
 * same source of truth — no api↔admin mirror drift.
 *
 * **Read-only.** The refund ACTION already exists (`POST /payments/:id/refund`,
 * AUT-105); this surface is the refunds-history list feeding the future
 * `/admin/refunds` page.
 *
 * **PII discipline (architect 🟠 — AUT-118):** refunds are CUSTOMER-side
 * (the recipient is the paying customer — the inverse of the AUT-117
 * payouts list, which is inspector-side). `recipientName` carries REDACTED
 * initials when the calling admin lacks `view_pii`. We surface ONLY the
 * recipient NAME — never phone / email (no DTO consumer needs them, and
 * `PaymentRefund.recipientId` is a bare UUID with no FK relation so the
 * lookup is a deliberate batch read, not a Prisma include). The
 * `piiRevealed` flag tells the SPA which variant it received so it never
 * re-redacts a full value or renders a redacted value as copyable. The
 * server is the sole authority on redaction.
 *
 * **NO `processedBy` (Igor default A — LOCKED):** `PaymentRefund` has no
 * `processedBy` / `adminId` column, the AUT-105 audit row is keyed by
 * `paymentId` (not refund id), and most refunds are listener-driven
 * (`order.cancelled` / `appeal.resolved`) with no admin actor at all.
 * Per-refund admin attribution is therefore impossible and is NOT faked
 * via an `admin_logs` join — the audit log remains the actor authority
 * (matches AUT-117 payouts list, which has no per-row actor either).
 *
 * All `Date` fields are ISO-8601 strings (JSON has no `Date`); consumers
 * convert at the render boundary via `new Date(dto.createdAt)`.
 */
/**
 * Admin-facing refund reason — the SIX wire-stable values. Mirror of the
 * Prisma `PaymentRefundReason` enum, confirmed 1:1 against the live schema
 * (no narrowing — architect lock #2):
 *
 *   - `customer_cancel_early`    — customer cancelled within the free window.
 *   - `customer_cancel_late`     — customer cancelled past the free window.
 *   - `inspector_cancel`         — inspector cancelled (customer made whole).
 *   - `dispute_inspector_fault`  — appeal resolved fully for the customer.
 *   - `dispute_partial`          — appeal resolved partially for the customer.
 *   - `admin_manual`             — operator-initiated refund via the existing
 *                                  `POST /payments/:id/refund` action.
 *
 * Single source of truth for the api's filter Zod schema too — the api
 * imports `ADMIN_REFUND_REASONS` rather than hand-typing the 6 values
 * (same pattern as `ADMIN_PAYOUT_STATUSES`).
 */
export const ADMIN_REFUND_REASONS = [
    'customer_cancel_early',
    'customer_cancel_late',
    'inspector_cancel',
    'dispute_inspector_fault',
    'dispute_partial',
    'admin_manual',
];
/**
 * Derived refund status. `PaymentRefund` has NO status column — the
 * outcome is derived from `processedAt` (matching the existing customer-
 * facing `toPaymentRefundDto` mapper):
 *
 *   - `completed` — the provider confirmed the refund (`processedAt !== null`).
 *   - `failed`    — the row was persisted for audit but the provider
 *                   returned `failed` (`processedAt === null`); support
 *                   reconciles manually.
 *
 * Wire-stable string union so the SPA can render a status chip without
 * importing `@prisma/client`.
 */
export const ADMIN_REFUND_STATUSES = ['completed', 'failed'];
//# sourceMappingURL=admin-refund.js.map